/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import { graphql } from 'gatsby';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';

import Layout from '../../Components/Core/Layout';
import FAQVideo from '../../Components/Custom/FAQ/FAQVideo';
import SEO from '../../Components/Core/Seo';
import BackToTop from '../../Components/Core/BackToTop';
import PageBuilder from '../../Components/Core/PageBuilder';
import FAQAnswer from '../../Components/Custom/FAQ/FAQAnswer';
import Feedback from '../../Components/Custom/Feedback';
import FAQSections from '../../Components/Custom/FAQ/FAQSections';
import ProTip from '../../Components/Custom/ProTip';
import { chart } from '../../utils/variation';

const pageTemplate = {
  'video-item': FAQVideo,
  'feedback-item': Feedback,
  'pro-tip': ProTip,
  'related-faqs': FAQSections,
  ...chart,
};

const FAQsAnswerPage = ({ location, data }) => {
  const {
    contentfulFaqItem: {
      question,
      questionSubheading,
      answer,
      faq_sub_category,
      proTipBlock,
      feedbackBlock,
      videoBlock,
      sections,
      internal: { type },
      pageSlug,
    },
    allContentfulFaqItem: { nodes },
    contentfulHeader,
    contentfulFooter,
  } = data;
  const intl = useIntl();

  let selectedQuestions;

  if (
    sections &&
    sections.find(({ __typename }) => __typename === 'ContentfulFaqItem')
  ) {
    selectedQuestions = sections.reduce(section => {
      if (section.component === 'related-faqs') {
        return section.faqItems;
      }
      return false;
    });
  }

  const relatedQuestions = selectedQuestions || nodes;

  const defaultData = {
    id: '',
    component: 'related-faqs',
    title: `${intl.formatMessage({ id: 'relatedQuestions' })}`,
    faqItems: relatedQuestions,
    // Remove all contact us from faq answers (needed to change backgroundColor of related questions)
    // backgroundColor: 'dark-grey',
    backgroundColor: 'default (grey)',
  };

  // Preparation for setting up re-orderable content in the FAQ answers
  // Adjust query in future to pull the Sections field
  const sectionData = [];

  if (feedbackBlock) {
    sectionData.push(...feedbackBlock);
  }
  if (videoBlock) {
    sectionData.push(...videoBlock);
  }
  // Remove all contact us from faq answers
  // sectionData.push(defaultData, contentfulSimpleBannerWithImage);
  if (defaultData.faqItems.length > 0) {
    sectionData.push(defaultData);
  }

  // Pull chart data out of `sections` if it exists to pass to FAQAnswer
  const chartData =
    sections &&
    sections.find(({ __typename }) => __typename === 'ContentfulProductChart');

  return (
    <Layout
      location={location}
      header={contentfulHeader}
      footer={contentfulFooter}
      contentType={type}
      languageToggle
    >
      <FAQAnswer
        question={question}
        questionSubheading={questionSubheading}
        answer={answer}
        category={faq_sub_category[0].subCategory}
        categorySlug={faq_sub_category[0].subCatPageSlug}
        proTip={proTipBlock}
        videoBlock={videoBlock}
        chart={chartData}
      />
      <PageBuilder
        sections={sectionData}
        template={pageTemplate}
        pageSlug={pageSlug}
      />
      <BackToTop />
    </Layout>
  );
};

FAQsAnswerPage.propTypes = {
  data: PropTypes.shape().isRequired,
  location: PropTypes.shape().isRequired,
};

export const query = graphql`
  query FAQItems(
    $locale: String = "en-CA"
    $contentful_id: String!
    $category: String!
  ) {
    contentfulFaqItem(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $locale }
    ) {
      pageSlug
      title
      metadescription
      schema {
        childMarkdownRemark {
          rawMarkdownBody
        }
      }
      question
      internal {
        type
      }
      questionSubheading {
        raw
      }
      faq_sub_category {
        subCategory
        subCatPageSlug
      }
      answer {
        raw
      }
      proTipBlock {
        id
        component
        proTipHeading
        proTipBody {
          raw
        }
        proTipIconDam {
          altText
          asset {
            localImage {
              publicURL
              childImageSharp {
                gatsbyImageData(layout: CONSTRAINED)
              }
            }
          }
        }
      }
      videoBlock {
        id
        component
        videoId
        videoLabel
        videoDescription {
          raw
        }
        transcript {
          raw
        }
        hideTranscriptLabel
        showTranscriptLabel
      }
      feedbackBlock {
        ...feedback_item
      }
      sections {
        ...video_item
        ...feedback_item
        ...pro_tip
        ...faq_popular_question
        ...product_chart
      }
    }
    allContentfulFaqItem(
      filter: {
        faq_sub_category: { elemMatch: { subCatPageSlug: { eq: $category } } }
        contentful_id: { nin: ["5fMwJSqygJLSk5vWUOE6pC", $contentful_id] }
        node_locale: { eq: $locale }
      }
      limit: 5
    ) {
      nodes {
        pageSlug
        question
        faq_sub_category {
          subCatPageSlug
        }
      }
    }
    contentfulHeader(
      contentful_id: { eq: "2DQ0umeJNyXVG1YNR3aj0U" }
      node_locale: { eq: $locale }
    ) {
      ...global_default_header
    }
    contentfulFooter(
      contentful_id: { eq: "2oVHC76EIxnAHufeI8QInp" }
      node_locale: { eq: $locale }
    ) {
      ...global_default_footer
    }
  }
`;

export default FAQsAnswerPage;

export const Head = ({ location, data, pageContext }) => {
  // Store a variable for possible regex matching string
  let regexMatched;
  // Parse the rich text object into HTML string
  const answerParsed = data.contentfulFaqItem.answer
    ? documentToHtmlString(JSON.parse(data.contentfulFaqItem.answer.raw))
    : '';

  if (answerParsed) {
    // Assign variable if it exists and based on language for Steps/Étapes
    if (pageContext.language === 'en') {
      regexMatched = answerParsed.match('<b>Step.*</ol>');
    } else {
      regexMatched = answerParsed.match('<b>Étapes.*</ol>');
    }
  }

  // schema answer assigned if string has an existing match else it's the parsed string
  const schemaAnswer = regexMatched ? regexMatched[0] : answerParsed;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: {
      '@type': 'Question',
      name: data.contentfulFaqItem.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          schemaAnswer ||
          data.contentfulFaqItem.schema.childMarkdownRemark.rawMarkdownBody,
      },
    },
  };

  return (
    <SEO
      title={data.contentfulFaqItem.title}
      description={data.contentfulFaqItem.metadescription}
      noIndex
      data={data.contentfulFaqItem}
      pageContext={pageContext}
      location={location}
    >
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </SEO>
  );
};
